// Canonical types shared by API and Web. Single source of truth.

export const ROLES = ['employee', 'it_reviewer', 'legal_reviewer', 'admin'] as const;
export type Role = (typeof ROLES)[number];

export const STATUSES = [
  'pending_it_review',
  'it_clarification_requested',
  'pending_legal_review',
  'legal_clarification_requested',
  'approved',
  'approved_with_conditions',
  'rejected',
] as const;
export type Status = (typeof STATUSES)[number];

export const EVENT_TYPES = [
  'submitted',
  'it_approved',
  'it_rejected',
  'it_clarification_requested',
  'submitter_responded',
  'legal_approved',
  'legal_approved_with_conditions',
  'legal_rejected',
  'legal_clarification_requested',
  'admin_edited',
  'vendor_intel_regenerated',
  'vendor_reviewer_note_changed',
  'feature_flag_changed',
  'notification_template_changed',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

// ── Vendor intelligence (CompanyBackground card) ──
export type Severity = 'clear' | 'caution' | 'critical' | 'unknown';

export const SECTION_KEYS = ['aiPolicy', 'dataPolicy', 'security', 'ethical', 'legal', 'track'] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export type ComplianceStatus =
  | 'aligned'
  | 'native'
  | 'baa'
  | 'type1'
  | 'type2'
  | 'limited'
  | 'no'
  | 'none'
  | 'na'
  | 'unknown';

export interface IntelSource {
  label: string;
  url: string;
}

export interface IntelBullet {
  label: string;
  value: string;
  severity?: Severity;
  // Required by policy for any bullet with severity 'caution' or 'critical'.
  // Bullets that arrive without one have their severity stripped server-side
  // so unsourced claims never render as red flags.
  source?: IntelSource;
}

export interface IntelSection {
  status: Severity;
  bullets: IntelBullet[];
  sources?: IntelSource[];
  compliance?: Record<'hipaa' | 'gdpr' | 'ccpa' | 'soc2', ComplianceStatus>;
}

export interface VendorIntelPayload {
  founded: string | number;
  hq: string;
  employees: string;
  summary: string;
  aiPolicy: IntelSection;
  dataPolicy: IntelSection;
  security: IntelSection;
  ethical: IntelSection;
  legal: IntelSection;
  track: IntelSection;
}

export interface VendorIntel {
  vendor: string;
  intel: VendorIntelPayload | null;
  status: 'pending' | 'ready' | 'failed';
  model: string | null;
  costUsd: number | null;
  error: string | null;
  generatedAt: string | null;
  regeneratedAt: string | null;
}

export interface VendorReviewerNote {
  id: number;
  vendor: string;
  section: SectionKey;
  note: string;
  updatedByName: string;
  updatedAt: string;
}

// ── 4-layer vendor-intel pipeline (Evidence → Threat → Rules → Reviewer) ──
//
// Layer 1 (Evidence): a model extracts verifiable facts only — no severity,
// no inference. Source on every finding when one exists.
// Layer 2 (Threat):   a model maps evidence to operational risk findings
// with severity *candidates* (advisory only — Layer 3 decides final).
// Layer 3 (Rules):    pure TypeScript walks the evidence + threat output
// and computes deterministic overall severity + procurement recommendation.
// Layer 4:            human IT/Legal reviewer (existing review action panel).

export const INTEL_LAYER_KEYS = ['evidence', 'threat', 'rules'] as const;
export type IntelLayerKey = (typeof INTEL_LAYER_KEYS)[number];

export type EvidenceType =
  | 'verified_fact'
  | 'public_claim'
  | 'third_party_report'
  | 'missing_evidence'
  | 'unknown';

export type Confidence = 'high' | 'medium' | 'low';

// Severity *candidate* used by Layer 2 (advisory). Layer 3 maps these
// onto the final 4-value Severity for the synthesized payload.
export type SeverityCandidate =
  | 'clear'
  | 'low'
  | 'moderate'
  | 'high'
  | 'critical'
  | 'unknown';

export const EVIDENCE_CATEGORIES = [
  'security',
  'compliance',
  'ai_policy',
  'data_governance',
  'operational',
  'public_reputation',
] as const;
export type EvidenceCategory = (typeof EVIDENCE_CATEGORIES)[number];

export interface EvidenceFinding {
  id: string;
  category: EvidenceCategory;
  finding: string;
  evidenceType: EvidenceType;
  source?: IntelSource;
  confidence: Confidence;
}

export interface EvidencePayload {
  vendor: {
    founded: string | number;
    hq: string;
    employees: string;
    summary: string;
  };
  findings: EvidenceFinding[];
  compliance: {
    hipaa: ComplianceStatus;
    gdpr: ComplianceStatus;
    ccpa: ComplianceStatus;
    soc2: ComplianceStatus;
    iso27001: ComplianceStatus;
  };
}

export interface ThreatFinding {
  id: string;
  riskArea: string;
  evidenceRefs: string[];
  operationalImplication: string;
  controlGap: string;
  severityCandidate: SeverityCandidate;
  confidence: Confidence;
}

export interface ThreatPayload {
  findings: ThreatFinding[];
}

export const RECOMMENDATIONS = [
  'approve',
  'approve_with_conditions',
  'needs_security_review',
  'needs_legal_review',
  'reject',
  'insufficient_evidence',
] as const;
export type Recommendation = (typeof RECOMMENDATIONS)[number];

export interface TriggeredRule {
  rule: string;
  severity: Severity;
  basis: string;
}

export interface RulesPayload {
  overallSeverity: Severity;
  recommendation: Recommendation;
  triggeredRules: TriggeredRule[];
}

export interface IntelLayer<T> {
  status: 'pending' | 'ready' | 'failed';
  payload: T | null;
  model: string | null;
  error: string | null;
  generatedAt: string | null;
  regeneratedAt: string | null;
}

export interface VendorIntelV2 {
  vendor: string;
  evidence: IntelLayer<EvidencePayload>;
  threat: IntelLayer<ThreatPayload>;
  rules: IntelLayer<RulesPayload>;
  // Synthesized VendorIntelPayload — what the collapsed card, queue badges,
  // and audit fan-out continue to consume. Built from evidence+threat+rules
  // by a pure function in the service layer.
  synthesized: VendorIntelPayload | null;
  notes: VendorReviewerNote[];
}

// ── Email notification templates (admin-editable) ──
//
// Brief specifies 5 admin-visible templates (submitted, it_approved,
// status_change, clarification, sla_exceeded). We ship a 6th
// (clarification_response) so today's IT/Legal "submitter responded"
// notifications remain admin-editable rather than getting orphaned.
// Internal event-type → template-key routing lives in notifications.service.
export const NOTIFICATION_TEMPLATE_KEYS = [
  'submitted',
  'it_approved',
  'status_change',
  'clarification',
  'clarification_response',
  'sla_exceeded',
] as const;
export type NotificationTemplateKey = (typeof NOTIFICATION_TEMPLATE_KEYS)[number];

export interface NotificationTemplate {
  id: string;
  key: NotificationTemplateKey;
  label: string;
  triggerDescription: string;
  recipientLabel: string;
  subjectTemplate: string;
  bodyTemplate: string;
  enabled: boolean;
  /** True when subject_template + body_template still match the seeded defaults. */
  isDefault: boolean;
  updatedByName: string | null;
  updatedAt: string;
}

// The 22 merge variables the editor exposes, grouped for display.
export const NOTIFICATION_VARIABLE_GROUPS = {
  Request: [
    'ticket_number',
    'vendor',
    'software_name',
    'category',
    'request_url',
    'intended_use',
  ],
  Submitter: ['submitter_name', 'submitter_first_name', 'submitter_email'],
  Reviewer: ['reviewer_name', 'reviewer_role', 'reviewer_email'],
  Comments: ['review_comment', 'review_conditions', 'submitter_response'],
  Status: ['decision', 'from_status', 'to_status'],
  SLA: ['sla_hours', 'sla_term_label', 'hours_overdue'],
} as const;

// ── Admin feature flags ──
export const FEATURE_FLAG_KEYS = [
  'vendor_intel_visible_to_employee',
  'vendor_intel_visible_to_it_reviewer',
  'vendor_intel_visible_to_legal_reviewer',
] as const;
export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

export interface FeatureFlag {
  key: FeatureFlagKey;
  value: boolean;
  updatedAt: string;
  updatedByName: string | null;
}

// ── SLA thresholds for reviewer queues (in hours) ──
// Defaults only. Live values come from the system_settings DB rows
// (sla_it_hours / sla_legal_hours) so an admin can tune them at runtime.
export const SLA_HOURS = { it: 72, legal: 120 } as const;

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  isActive: boolean;
  createdAt: string;
  mustChangePassword?: boolean;
}

export const CATEGORIES = [
  'Clinical Imaging AI',
  'Diagnostic AI',
  'Clinical Documentation',
  'General Purpose AI',
  'Scheduling & Practice Mgmt',
  'Image Library / Reference',
  'Communication',
  'Productivity',
  'Translation',
  'Research',
  'Other',
] as const;
export type Category = (typeof CATEGORIES)[number];

export interface RequestRecord {
  id: string;
  ticketNumber: string;
  submitterId: string;
  submitterName: string;
  submitterEmail: string;
  softwareName: string;
  vendor: string | null;
  category: Category;
  logoUrl: string | null;
  websiteUrl: string;
  intendedUse: string;
  dataInputs: string;
  expectedOutputs: string;
  usesConfidentialData: boolean;
  usesPrivilegedData: boolean;
  usesCustomerData: boolean;
  outputUsage: string;
  additionalNotes: string | null;
  status: Status;
  currentReviewerId: string | null;
  conditions: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export interface StatusEvent {
  id: number;
  requestId: string;
  actorId: string;
  actorName: string;
  actorRole: Role | 'system';
  eventType: EventType;
  fromStatus: Status | null;
  toStatus: Status;
  comment: string | null;
  conditions: string | null;
  createdAt: string;
}

export type Risk = 'low' | 'med' | 'high';

export interface RegistryRow {
  id: string;
  ticketNumber: string;
  softwareName: string;
  vendor: string | null;
  category: Category;
  logoUrl: string | null;
  websiteUrl: string;
  description: string;
  status: Status;
  risk: Risk;
  usesConfidentialData: boolean;
  usesCustomerData: boolean;
  createdAt: string;
  updatedAt: string;
  submitterName: string;
}

export interface ApiEnvelope<T> {
  data: T;
  meta?: { page?: number; total?: number; pageSize?: number };
}

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}
